import CloseFullscreenRounded from '@mui/icons-material/CloseFullscreenRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import OpenInFullRounded from '@mui/icons-material/OpenInFullRounded';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type React from 'react';
import type { JSX } from 'react';
import { useEffect } from 'react';
import { useGetWidgetConfigQuery } from 'services/api/demochat';
import { NAVY, STATUS_GOOD, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

import { canTalkToHost, requestClose, requestToggleExpand, useHostWindowState } from './hostBridge';

interface Props {
  companyIdentifier: string | undefined;
  children: JSX.Element;
}

const JOST_FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Jost:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400&display=swap';

// Load the Jost font only while the widget is mounted, instead of app-wide in index.html.
const useJostFont = () => {
  useEffect(() => {
    if (document.querySelector(`link[href="${JOST_FONT_HREF}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = JOST_FONT_HREF;
    document.head.appendChild(link);
  }, []);
};

const initialsOf = (name: string): string =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');

const ChatWrapper: React.FC<Props> = ({ companyIdentifier, children }) => {
  useJostFont();
  const { expanded: isExpanded, expandable: canExpand } = useHostWindowState();
  const { data: widgetConfig } = useGetWidgetConfigQuery(
    { companyIdentifier: companyIdentifier ?? '' },
    { skip: !companyIdentifier }
  );

  const displayName = widgetConfig?.displayName || 'Chat';
  const subtitle = widgetConfig?.subtitle || 'En línea';
  const avatarUrl = widgetConfig?.avatarUrl ?? undefined;
  const theme = widgetConfig?.theme;

  const headerBackground = theme?.headerBackground || NAVY;
  const headerTextColor = theme?.headerTextColor || SURFACE_CONTAINER_LOWEST;
  const onlineDotColor = theme?.onlineDotColor || STATUS_GOOD;
  const fontFamily = theme?.fontFamily || undefined;
  const mutedHeaderTextColor = alpha(headerTextColor, 0.72);

  const headerActionSx = {
    color: headerTextColor,
    width: 34,
    height: 34,
    backgroundColor: alpha(headerTextColor, 0.1),
    '&:hover': { backgroundColor: alpha(headerTextColor, 0.2) },
  };

  return (
    <Box
      sx={{
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: SURFACE_CONTAINER_LOWEST,
        ...(fontFamily && {
          fontFamily,
          '& .MuiTypography-root, & .MuiInputBase-input, & .MuiButton-root, & input, & textarea': {
            fontFamily,
          },
        }),
      }}
    >
      <Box
        sx={{
          backgroundColor: headerBackground,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          width: '100%',
          flexShrink: 0,
          py: 1.5,
          px: 2,
          color: headerTextColor,
          zIndex: 1,
        }}
      >
        <Avatar
          src={avatarUrl}
          sx={{
            bgcolor: avatarUrl ? 'transparent' : alpha(headerTextColor, 0.16),
            color: headerTextColor,
            width: 42,
            height: 42,
            fontSize: '0.95rem',
            fontWeight: 600,
          }}
        >
          {initialsOf(displayName)}
        </Avatar>
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '1.05rem', lineHeight: 1.25, color: headerTextColor }} noWrap>
            {displayName}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: onlineDotColor, flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.78rem', color: mutedHeaderTextColor }} noWrap>
              {subtitle}
            </Typography>
          </Box>
        </Box>
        {canTalkToHost() && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
            {canExpand && (
              <Tooltip title={isExpanded ? 'Reducir' : 'Ampliar'}>
                <IconButton
                  aria-label={isExpanded ? 'Reducir ventana del chat' : 'Ampliar ventana del chat'}
                  onClick={requestToggleExpand}
                  sx={headerActionSx}
                >
                  {isExpanded ? (
                    <CloseFullscreenRounded sx={{ fontSize: '1.05rem' }} />
                  ) : (
                    <OpenInFullRounded sx={{ fontSize: '1.05rem' }} />
                  )}
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Cerrar">
              <IconButton aria-label="Cerrar chat" onClick={requestClose} sx={headerActionSx}>
                <CloseRounded sx={{ fontSize: '1.25rem' }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default ChatWrapper;
